/**
 * TrainingBoard — Game display for training mode.
 *
 * Always uses the cards-on-table layout (replay-style positions).
 * - "cards_on_table": all 4 hands always visible
 * - "classic": only human-controlled hands are visible, one at a time
 */

import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import Card from './Card';
import CoachContextMenu from './CoachContextMenu';
import type { CardNumber } from '../types/game';
import type { TrainingDisplayMode } from '../types/app';
import type { Socket } from 'socket.io-client';
import { sortHand } from '../utils/cards';

type ScreenPosition = 'bottom' | 'top' | 'left' | 'right';
const CARD_ASPECT = 60 / 109;

interface TrainingBoardProps {
  displayMode: TrainingDisplayMode;
  viewPosition: number;
  players: Array<{ pseudo: string; position: number; isBot: boolean }>;
  allHands: Record<number, CardNumber[]>;
  legalCards: Record<number, CardNumber[]>;
  currentPlayer: number;
  currentTrick: CardNumber[];
  trickPlayerPositions: number[];
  phase: number | string;
  taker: number | null;
  currentContract?: number;
  completedTricks: Array<{ cards: CardNumber[]; players: number[]; winner?: number }>;
  disabledBots: Set<number>;
  humanAutomated: boolean;
  randomFallbackCards: Set<CardNumber>;
  reviewTrick: { cards: CardNumber[]; players: number[]; trickNumber: number } | null;
  reviewPhase?: 
    | { type: 'chien' | 'ecart'; cards: CardNumber[] }
    | { type: 'bids'; bids: Array<{ player: number; bid: number }> }
    | null;
  onClearReview: () => void;
  onRewindCard: (positionInTrick: number, trickNumber?: number) => void;
  socket: Socket | null;
  onPlayCard: (card: CardNumber, seat?: number) => void;
  ecartMode?: boolean;
  ecartSelectedCards?: CardNumber[];
  discardableCards?: Set<CardNumber>;
  onToggleEcartCard?: (card: CardNumber) => void;
  humanControlledPositions?: Set<number>;
  reviewHands?: Record<number, CardNumber[]> | null;
  reviewAnimIndex?: number;
  onBoardClick?: () => void;
  gameSpeed?: string;
  hasTopBanner?: boolean;
  awaitingAdvance?: boolean;
  highlightedCard?: CardNumber | null;
  feedbackSeat?: number | null;
  mlSuggestedCards?: Map<CardNumber, number>;
  /** When the displayed "current trick" is actually the last completed trick,
   *  pass its 1-indexed trick number so the context menu rewinds to the right trick. */
  displayedTrickIsCompleted?: number;
  onCommentCard?: (trickNumber: number, positionInTrick: number) => void;
  onCommentTrick?: (trickNumber: number) => void;
  feedbackEnabledSeats?: Set<number>;
  onToggleFeedbackSeat?: (seat: number) => void;
  feedbackMode?: boolean;
  /** Clés `${trickNumber}-${positionInTrick}-${card}` pour halo drill métrique (replay ML) */
  metricHighlightKeys?: Set<string>;
  /** Numéro du pli affiché au centre (null si aucune carte de pli au centre) */
  metricTableTrickNumber?: number | null;
}

function metricCardHighlighted(
  keys: Set<string> | undefined,
  trickNum: number | null | undefined,
  idx: number,
  card: CardNumber,
): boolean {
  if (!keys?.size || trickNum == null) return false;
  return keys.has(`${trickNum}-${idx}-${card}`);
}

function getScreenPositions(myPosition: number): Map<number, ScreenPosition> {
  const positions = new Map<number, ScreenPosition>();
  const layout: ScreenPosition[] = ['bottom', 'right', 'top', 'left'];
  for (let i = 0; i < 4; i++) {
    const relativePos = (i - myPosition + 4) % 4;
    positions.set(i, layout[relativePos]);
  }
  return positions;
}

export default function TrainingBoard({
  displayMode, viewPosition, players, allHands, legalCards,
  currentPlayer, currentTrick, trickPlayerPositions, phase,
  taker, currentContract, disabledBots,
  humanAutomated, randomFallbackCards, reviewTrick, reviewPhase, onClearReview,
  onRewindCard, socket, onPlayCard,
  ecartMode, ecartSelectedCards, discardableCards, onToggleEcartCard,
  humanControlledPositions, reviewHands, reviewAnimIndex, onBoardClick,
  gameSpeed: _gameSpeed, hasTopBanner, awaitingAdvance, highlightedCard: _highlightedCard,
  feedbackSeat, mlSuggestedCards, displayedTrickIsCompleted,
  onCommentCard, onCommentTrick,
  feedbackEnabledSeats, onToggleFeedbackSeat, feedbackMode: boardFeedbackMode,
  metricHighlightKeys, metricTableTrickNumber,
}: TrainingBoardProps) {
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; card: number; player: number; playerName: string;
    trickNumber: number; positionInTrick?: number;
  } | null>(null);

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(600);
  const [tableWidth, setTableWidth] = useState(800);

  const measure = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight);
      setTableWidth(tableRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (tableRef.current) ro.observe(tableRef.current);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [measure]);

  const { cardWidth, cardHeight, borderRadius } = useMemo(() => {
    const h1 = tableHeight / 5;
    const w2 = tableWidth / 9;
    const h2 = w2 / CARD_ASPECT;
    const h = Math.min(h1, h2);
    const w = h * CARD_ASPECT;
    return {
      cardWidth: Math.round(w),
      cardHeight: Math.round(h),
      borderRadius: Math.max(2, Math.round(w * 0.133)),
    };
  }, [tableHeight, tableWidth]);

  const screenPositions = useMemo(() => getScreenPositions(viewPosition), [viewPosition]);

  const isPlayPhase = phase === 'playing' || phase === 6;
  const isBiddingPhase = phase === 'bidding';
  const ecartSet = useMemo(() => new Set(ecartSelectedCards ?? []), [ecartSelectedCards]);

  const displayHands = (reviewTrick || reviewPhase) && reviewHands ? reviewHands : allHands;

  const playableSeats = useMemo(() => {
    const seats = new Set<number>();
    if (!humanAutomated) seats.add(0);
    for (const seat of disabledBots) seats.add(seat);
    if (feedbackSeat != null) seats.add(feedbackSeat);
    return seats;
  }, [disabledBots, humanAutomated, feedbackSeat]);

  const visiblePositions = useMemo(() => {
    if (displayMode === 'cards_on_table') return new Set([0, 1, 2, 3]);
    if (ecartMode && taker !== null) {
      const s = new Set(playableSeats);
      s.add(taker);
      return s;
    }
    // In feedback mode, show only the feedback seat's hand (like a human-controlled seat)
    if (feedbackSeat != null) {
      return new Set([feedbackSeat]);
    }
    if (playableSeats.size === 0) return new Set<number>();
    if (playableSeats.size === 1) return new Set(playableSeats);
    return playableSeats.has(currentPlayer) ? new Set([currentPlayer]) : new Set<number>();
  }, [displayMode, playableSeats, currentPlayer, ecartMode, taker, feedbackSeat]);

  const handleCardContextMenu = useCallback((
    e: React.MouseEvent, card: number, playerPos: number, trickNum: number, posInTrick?: number,
  ) => {
    e.preventDefault();
    const playerName = players.find(p => p.position === playerPos)?.pseudo ?? `Joueur ${playerPos}`;
    setContextMenu({
      x: e.clientX, y: e.clientY, card, player: playerPos,
      playerName, trickNumber: trickNum, positionInTrick: posInTrick,
    });
  }, [players]);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const tableStyle = useMemo(() => ({
    '--card-small-width': `${cardWidth}px`,
    '--card-small-height': `${cardHeight}px`,
    '--card-border-radius': `${borderRadius}px`,
  } as React.CSSProperties), [cardWidth, cardHeight, borderRadius]);

  return (
    <div className={`training-board ${displayMode === 'cards_on_table' ? 'cards-on-table' : 'training-classic'} ${hasTopBanner ? 'has-top-banner' : ''}`} ref={tableRef} style={tableStyle} onClick={onBoardClick}>
      {[0, 1, 2, 3].map(position => {
          const hand = sortHand(displayHands[position] ?? []);
          const screenPos = screenPositions.get(position) ?? 'bottom';
          const player = players.find(p => p.position === position);
          const isTaker = position === taker;
          const isCurrentlyPlaying = position === currentPlayer && isPlayPhase;
          const isBiddingCurrent = position === currentPlayer && isBiddingPhase;
          const isHumanControlled = humanControlledPositions?.has(position) ?? false;
          const canPlay = isCurrentlyPlaying && playableSeats.has(position) && !reviewTrick;
          const isEcartTarget = ecartMode && position === taker;
          const legal = legalCards[position] ?? [];
          const legalSet = new Set(legal);
          const isVisible = visiblePositions.has(position);

          const isSide = screenPos === 'left' || screenPos === 'right';
          const isRight = screenPos === 'right';
          const rotatedVisualHeight = cardWidth;

          const filteredHand = isEcartTarget ? hand.filter(c => !ecartSet.has(c)) : hand;

          const sideVertMargin = tableWidth >= tableHeight
            ? Math.round(cardWidth / 3)
            : Math.round(cardHeight * 1.5);

          const hMarginPerSide = tableWidth >= tableHeight
            ? Math.round(cardHeight * 1.0)
            : Math.round(cardWidth * 0.5);
          const horizontalAvailable = Math.max(cardWidth * 3, tableWidth - 2 * hMarginPerSide);

          const sideWrapperHeight = (() => {
            if (!isSide || filteredHand.length <= 0) return rotatedVisualHeight;
            const available = tableHeight - sideVertMargin * 2 - 20;
            if (available <= 0) return 20;
            const spacing = available / filteredHand.length;
            return Math.max(12, Math.min(rotatedVisualHeight, spacing));
          })();

          const horizontalOverlap = (() => {
            if (isSide || filteredHand.length <= 1) return 0;
            const spacingPerCard = horizontalAvailable / filteredHand.length;
            const overlap = spacingPerCard - cardWidth;
            return Math.max(-(cardWidth - 8), Math.min(0, overlap));
          })();

          const displayCards = isRight ? [...filteredHand].reverse() : filteredHand;
          const cardsStyle: React.CSSProperties | undefined = isSide
            ? { width: `${cardWidth}px`, minWidth: `${cardWidth}px`, maxWidth: `${cardWidth}px` }
            : undefined;
          const handStyle: React.CSSProperties | undefined = !isSide
            ? { maxWidth: `${horizontalAvailable}px` }
            : undefined;

          return (
            <div key={position} className={`replay-hand replay-hand-${screenPos}`} style={handStyle}>
              {isVisible && (
                <div className="replay-hand-cards" style={cardsStyle}>
                  {displayCards.map((card, idx) => {
                    const isLegal = canPlay && legalSet.has(card);
                    const isDisabled = canPlay && !isLegal;
                    const isDiscardable = isEcartTarget && discardableCards?.has(card);
                    const isEcartDisabled = isEcartTarget && !discardableCards?.has(card);
                    const style: React.CSSProperties = {};

                    if (isSide) {
                      style.height = `${sideWrapperHeight}px`;
                      style.zIndex = isRight ? displayCards.length - idx : idx + 1;
                    } else if (idx > 0) {
                      style.marginLeft = `${horizontalOverlap}px`;
                    }

                    const isFeedbackTarget = feedbackSeat === position && feedbackSeat != null;
                    const mlConfidence = isFeedbackTarget ? mlSuggestedCards?.get(card) : undefined;
                    const isMlSuggested = mlConfidence !== undefined;
                    const cardClickable = isLegal || isDiscardable || isFeedbackTarget;
                    const cardDisabled = isDisabled || isEcartDisabled;
                    const handleClick = isLegal
                      ? () => onPlayCard(card, position)
                      : isDiscardable
                        ? () => onToggleEcartCard?.(card)
                        : isFeedbackTarget
                          ? () => onPlayCard(card, position)
                          : undefined;

                    const wrapperStyle: React.CSSProperties = { ...style };
                    if (isMlSuggested) {
                      (wrapperStyle as Record<string, unknown>)['--ml-confidence'] = mlConfidence;
                    }

                    const liftClass = isMlSuggested
                      ? `training-card-ml-lift training-card-ml-lift-${screenPos}`
                      : '';

                    return (
                      <div
                        key={`${card}-${idx}`}
                        className={`replay-card-wrapper ${cardClickable ? 'training-card-clickable' : ''} ${cardDisabled ? 'training-card-disabled' : ''} ${isMlSuggested ? 'training-card-ml-suggested' : ''} ${liftClass} ${isFeedbackTarget ? 'training-card-feedback-target' : ''}`}
                        style={wrapperStyle}
                        onClick={handleClick}
                        title={isFeedbackTarget ? 'Cliquer pour classer cette carte' : undefined}
                      >
                        <Card card={card} size="small" />
                      </div>
                    );
                  })}
                </div>
              )}
              <span
                className={`replay-hand-label ${isTaker ? 'is-taker' : ''} ${isCurrentlyPlaying ? 'is-current' : ''} ${isBiddingCurrent && isHumanControlled ? 'is-bidding-human' : ''} ${isBiddingCurrent ? 'is-bidding' : ''} ${boardFeedbackMode && feedbackEnabledSeats?.has(position) ? 'feedback-active' : ''}`}
                onClick={boardFeedbackMode && onToggleFeedbackSeat ? (e) => { e.stopPropagation(); onToggleFeedbackSeat(position); } : undefined}
                style={boardFeedbackMode && onToggleFeedbackSeat ? { cursor: 'pointer' } : undefined}
                title={boardFeedbackMode ? (feedbackEnabledSeats?.has(position) ? 'Cliquer pour désactiver le feedback expert pour ce joueur' : 'Cliquer pour activer le feedback expert pour ce joueur') : undefined}
              >
                {boardFeedbackMode && feedbackEnabledSeats
                  ? <span className={`feedback-seat-indicator ${feedbackEnabledSeats.has(position) ? 'active' : ''}`}>
                      {feedbackEnabledSeats.has(position) ? '●' : '○'}
                    </span>
                  : <span className="feedback-seat-indicator" style={{ visibility: 'hidden' }}>○</span>
                }
                {player?.pseudo ?? `P${position}`}
                {isTaker && currentContract ? ` (${contractLabel(currentContract)})` : ''}
                {isCurrentlyPlaying ? ' *' : ''}
                {!isVisible && ` — ${hand.length} cartes`}
              </span>
            </div>
          );
        })}

        {/* Trick center (current trick, phase review, or trick review) */}
        {reviewPhase?.type === 'bids' ? (
          <div className="replay-trick-center training-trick-reviewing training-phase-reviewing">
            <div className="training-trick-review-label">Enchères</div>
            <div className="training-bids-review">
              {reviewPhase.bids.map((b, i) => {
                const pName = players.find(pl => pl.position === b.player)?.pseudo ?? `P${b.player}`;
                return (
                  <div key={i} className="training-bids-review-row">
                    <span className="training-bids-review-name">{pName}</span>
                    <span className={`training-bids-review-bid ${b.bid === 0 ? 'pass' : 'bid'}`}>
                      {bidReviewLabel(b.bid)}
                    </span>
                  </div>
                );
              })}
            </div>
            <button className="training-trick-review-close" onClick={onClearReview}>
              Retour au jeu
            </button>
          </div>
        ) : reviewPhase ? (
          <div className="replay-special-cards">
            <span className="replay-special-label">
              {reviewPhase.type === 'chien' ? 'Chien' : 'Écart'}
            </span>
            <div className="replay-special-cards-row">
              {reviewPhase.cards.map((card, idx) => (
                <Card key={`phase-${card}-${idx}`} card={card} size="small" />
              ))}
            </div>
            <button className="training-trick-review-close" onClick={onClearReview}>
              Retour au jeu
            </button>
          </div>
        ) : reviewTrick ? (
          <div className="replay-trick-center training-trick-reviewing">
            {reviewTrick.cards.slice(0, reviewAnimIndex ?? reviewTrick.cards.length).map((card, idx) => {
              const playerPos = reviewTrick.players[idx];
              const pos = screenPositions.get(playerPos) ?? 'bottom';
              const isFallback = randomFallbackCards.has(card);
              const metricGlow = metricCardHighlighted(
                metricHighlightKeys, reviewTrick.trickNumber, idx, card,
              );
              return (
                <div
                  key={`review-${card}-${idx}`}
                  className={`replay-trick-slot replay-trick-slot-${pos} ${isFallback ? 'training-random-fallback' : ''} ${metricGlow ? 'training-metric-drill-glow' : ''}`}
                  style={{ zIndex: idx + 1 }}
                  onClick={e => handleCardContextMenu(e, card, playerPos, reviewTrick.trickNumber, idx)}
                  onContextMenu={e => handleCardContextMenu(e, card, playerPos, reviewTrick.trickNumber, idx)}
                >
                  <Card card={card} size="small" />
                </div>
              );
            })}
            <button className="training-trick-review-close" onClick={onClearReview}>
              Retour au pli en cours
            </button>
          </div>
        ) : currentTrick.length > 0 ? (
          <div className="replay-trick-center">
            {currentTrick.map((card, idx) => {
              const playerPos = trickPlayerPositions[idx];
              const pos = screenPositions.get(playerPos) ?? 'bottom';
              const isFallback = randomFallbackCards.has(card);
              const tableTn =
                metricTableTrickNumber ??
                (displayedTrickIsCompleted ?? null);
              const metricGlow = metricCardHighlighted(metricHighlightKeys, tableTn, idx, card);
              return (
                <div
                  key={`${card}-${idx}`}
                  className={`replay-trick-slot replay-trick-slot-${pos} ${isFallback ? 'training-random-fallback' : ''} ${metricGlow ? 'training-metric-drill-glow' : ''}`}
                  style={{ zIndex: idx + 1 }}
                  onClick={e => handleCardContextMenu(e, card, playerPos, displayedTrickIsCompleted ?? 0, idx)}
                  onContextMenu={e => handleCardContextMenu(e, card, playerPos, displayedTrickIsCompleted ?? 0, idx)}
                >
                  <Card card={card} size="small" />
                </div>
              );
            })}
          </div>
        ) : null}

        {awaitingAdvance && (
          <div className="training-click-prompt">Cliquez pour continuer</div>
        )}

        {/* Context menu */}
        {contextMenu && (
          <CoachContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            card={contextMenu.card}
            player={contextMenu.player}
            playerName={contextMenu.playerName}
            trickNumber={contextMenu.trickNumber}
            positionInTrick={contextMenu.positionInTrick}
            socket={socket}
            onClose={closeContextMenu}
            onRewindCard={onRewindCard}
            onCommentCard={onCommentCard}
            onCommentTrick={onCommentTrick}
          />
        )}
      </div>
  );
}

function bidReviewLabel(bid: number): string {
  switch (bid) {
    case 0: return 'Passe';
    case 1: return 'Petite';
    case 2: return 'Garde';
    case 3: return 'Garde sans';
    case 4: return 'Garde contre';
    default: return String(bid);
  }
}

function contractLabel(contract: number): string {
  switch (contract) {
    case 1: return 'petite';
    case 2: return 'garde';
    case 3: return 'garde sans';
    case 4: return 'garde contre';
    default: return '';
  }
}
